import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "../client";
import type {
  DebarmentStatus,
  Investigator,
  InvestigatorCreateRequest,
  InvestigatorDetail,
  InvestigatorListRow,
  InvestigatorUpdateRequest,
} from "../types";

const KEY = ["investigators"] as const;

export interface InvestigatorFilters {
  search?: string;
  debarment_status?: DebarmentStatus;
  is_active?: boolean;
}

function buildQuery(filters?: InvestigatorFilters): string {
  if (!filters) return "";
  const params = new URLSearchParams();
  if (filters.search) params.set("search", filters.search);
  if (filters.debarment_status)
    params.set("debarment_status", filters.debarment_status);
  if (filters.is_active !== undefined)
    params.set("is_active", String(filters.is_active));
  const s = params.toString();
  return s ? `?${s}` : "";
}

export function useInvestigators(filters?: InvestigatorFilters) {
  return useQuery<InvestigatorListRow[]>({
    queryKey: [...KEY, filters ?? {}],
    queryFn: () => apiRequest(`/api/investigators${buildQuery(filters)}`),
  });
}

export function useInvestigator(id: string | undefined) {
  return useQuery<InvestigatorDetail>({
    queryKey: [...KEY, id],
    queryFn: () => apiRequest(`/api/investigators/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreateInvestigator() {
  const qc = useQueryClient();
  return useMutation<Investigator, Error, InvestigatorCreateRequest>({
    mutationFn: (payload) =>
      apiRequest("/api/investigators", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateInvestigator(id: string) {
  const qc = useQueryClient();
  return useMutation<Investigator, Error, InvestigatorUpdateRequest>({
    mutationFn: (payload) =>
      apiRequest(`/api/investigators/${id}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      }),
    onSuccess: (inv) => {
      qc.setQueryData([...KEY, id], inv);
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}
